import React from 'react';
import { AreaClosed, LinePath } from '@vx/shape';
import { scaleLinear, scaleUtc } from 'd3-scale';
import { extent, pairs, range, zip } from 'd3-array';
import styled, { StyledComponentClass } from 'styled-components';

import { colors } from '../../styles';
import { GraphProps } from '../../types/graphs';
import { Hoverable } from '../../utils/hoverable';
import { padding } from '../../components/graphs/utils';
import {
  XAxis,
  Group,
  HoverableGroup,
  PointMarkers,
  VerticalAxis,
  ResponsiveSVG,
} from '../../atoms/graphs';

export type XValue = number | Date;
export type Interval = [any, any];
export type SegmentCallback = (intervals: Interval[]) => void;
export type SegmentPredicate = (intervals: Interval[]) => boolean;

export interface Props extends GraphProps {
  data: any[][];
  segmentClicked?: SegmentCallback;
  segmentHovered?: SegmentCallback;
  segmentUnhovered?: SegmentCallback;
  outerPadding?: number[];
  isDefined?: (d: any) => boolean;
  isEstimated?: (d: any) => boolean;
  segmentFocused?: SegmentPredicate;
  seriesNames?: string[];
  showGridLines?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  xAxisPadding?: number;
  yAxisPadding?: number;
  xLabel?: (d: any) => string;
  xValue?: (d: any) => XValue;
  yValue?: (d: any) => number;
}

const ident = x => x;
const noop = () => undefined;

class AreaComparison extends React.Component<Props, any> {
  static defaultProps: Props = {
    data: [[], []],
    height: 200,
    segmentClicked: noop,
    segmentHovered: noop,
    segmentUnhovered: noop,
    outerPadding: [1, 30, 30],
    isDefined: () => true,
    isEstimated: noop,
    segmentFocused: noop,
    seriesNames: ['You', 'Similar fleets'],
    showXAxis: true,
    showYAxis: true,
    showGridLines: true,
    width: 600,
    xAxisPadding: 8,
    yAxisPadding: 15,
    xLabel: String,
    xValue: ident,
    yValue: ident,
  };

  public render() {
    const {
      className,
      data,
      height,
      segmentClicked,
      segmentHovered,
      segmentUnhovered,
      isDefined,
      isEstimated,
      segmentFocused,
      seriesNames,
      showXAxis,
      showYAxis,
      showGridLines,
      outerPadding,
      width,
      xAxisPadding,
      yAxisPadding,
      xLabel,
      xValue,
      yValue,
    } = this.props;

    const majorSeries = data[0];
    const xValues = majorSeries.map(xValue);
    const intervalsBySegment = zip(...data.map(d => pairs(d)));
    const seriesName = (d, i) => seriesNames[i];

    const [topPadding, rightPadding, bottomPadding, leftPadding] = padding(
      outerPadding,
    );
    const widthLessPadding = width - leftPadding - rightPadding;
    const heightLessPadding = height - topPadding - bottomPadding;
    const segmentWidth = widthLessPadding / intervalsBySegment.length;
    const graphTop = topPadding;
    const graphBottom = height - bottomPadding;
    const graphLeft = leftPadding;
    const graphRight = width - rightPadding;

    const yScale = scaleLinear()
      .range([-graphTop, -graphBottom])
      .domain([0, 100]);

    const xScale = scaleUtc()
      .range([graphLeft, graphRight])
      .domain(extent(xValues));

    const dataProps = {
      x: xValue,
      y: yValue,
      xScale,
      yScale,
    };

    return (
      <ResponsiveSVG
        className={className}
        height={height}
        width={width}
        viewBox={`0 0 ${width} ${height}`}
      >
        <g>
          <Group
            className="graph"
            y={graphTop}
            {...{ height: heightLessPadding, width: widthLessPadding }}
          >
            {showGridLines && (
              <Group className="grid-lines" y={graphBottom}>
                {range(0, 110, 10).map(y => (
                  <line
                    key={`grid-line-${y}`}
                    y1={yScale(y)}
                    y2={yScale(y)}
                    x1={graphLeft}
                    x2={graphRight}
                  />
                ))}
              </Group>
            )}

            {intervalsBySegment.reverse().map((intervals, i) => {
              const isLastSegment = i === intervalsBySegment.length - 1;
              const [x0, x1] = intervals[0].map(d => xScale(xValue(d)));
              const [startValues, endValues] = zip(...intervals);

              const focusedClass = segmentFocused(intervals) ? 'focused' : '';
              const onSegmentClick = () => segmentClicked(intervals);

              const Segment = Hoverable(HoverableGroup, {
                hoverIn: () => segmentHovered(intervals),
                hoverOut: () => segmentUnhovered(intervals),
                svg: true,
              });

              const intervalShapes = intervals.map((interval, j) => {
                if (!interval.every(isDefined)) {
                  return [null, null];
                }
                const estClass = isEstimated(interval[1]) ? 'estimated' : '';
                const rank = j === 0 ? 'major' : 'minor';

                return [
                  <AreaClosed
                    key={`${rank}-${j}`}
                    className={`area ${rank} ${estClass}`}
                    {...dataProps}
                    data={interval}
                  />,
                  <LinePath
                    key={`${rank}-${j}`}
                    className={`border-line ${rank}`}
                    {...dataProps}
                    data={interval}
                  />,
                ];
              });
              const [[majorArea, ...minorAreas], borderLines] = zip(
                ...intervalShapes,
              );

              return [
                <Segment
                  className={`segment ${focusedClass}`}
                  key={`segment-${i}`}
                  x={x0}
                  onClick={onSegmentClick}
                  height={heightLessPadding}
                  width={segmentWidth}
                >
                  {segmentWasHovered => (
                    <Group y={graphBottom}>
                      <Group x={-x0}>
                        {minorAreas}
                        {majorArea}
                        {borderLines}
                      </Group>
                      <Group x={x1 - x0}>
                        <PointMarkers
                          {...{ isDefined, isEstimated, yScale }}
                          data={endValues}
                          value={yValue}
                          name={seriesName}
                        />
                      </Group>
                    </Group>
                  )}
                </Segment>,
                isLastSegment && (
                  <Group x={x0} y={graphBottom} key="last-segment">
                    <PointMarkers
                      {...{ isDefined, isEstimated, yScale }}
                      data={startValues}
                      value={yValue}
                      name={seriesName}
                    />
                  </Group>
                ),
              ];
            })}
          </Group>
          {showXAxis && (
            <Group y={graphBottom + xAxisPadding}>
              <XAxis
                data={majorSeries}
                label={xLabel}
                value={xValue}
                scale={xScale}
              />
            </Group>
          )}
          {showYAxis && (
            <g>
              <Group
                className="y-axis"
                x={graphLeft - yAxisPadding}
                y={graphBottom}
              >
                <VerticalAxis ticks={range(10, 100, 20)} scale={yScale} />
              </Group>
              <Group
                className="y-axis"
                x={graphRight + yAxisPadding}
                y={graphBottom}
              >
                <VerticalAxis ticks={range(10, 100, 20)} scale={yScale} />
              </Group>
            </g>
          )}
        </g>
      </ResponsiveSVG>
    );
  }
}

export default styled(AreaComparison)`
  .grid-lines {
    stroke: ${colors.grey50};
    stroke-width: 1;
  }

  .major {
    fill: ${colors.green300};
  }

  .focused .major,
  .hovered .major {
    fill: ${colors.green500};
  }

  .minor {
    fill: ${colors.green100};
  }

  .focused .minor,
  .hovered .minor {
    fill: ${colors.green200};
  }

  .area.estimated {
    fill: ${colors.grey500};
  }

  .segment {
    cursor: pointer;
  }

  .area {
    stroke-width: 0;
  }

  .focused .area,
  .hovered .area {
    fill-opacity: 1;
  }

  .border-line {
    stroke-opacity: 0.2;
    stroke: white;
  }

  .border-line.major {
    visibility: hidden;
  }

  .marker {
    stroke: white;
    stroke-width: 2;

    &.major,
    &.minor {
      cursor: pointer;
    }
  }

  .tick {
    fill: ${colors.grey800};
  }

  .y-axis .tick {
    font-size: 0.6em;
  }
`;
